<template>
    <section>
        <div class="card-tabs">
            <nav class="card-title-tabs" role="tablist">
                <span class="card-title-tab" role="tab" tabindex="0"
                    v-for="tab in tabs"
                    v-bind:key="tab.key"
                    v-on:click="activeTab = tab.key"
                    v-on:keyup.enter="activeTab = tab.key"
                    v-bind:id="`tab_switcher_${tab.key}`"
                    v-bind:aria-selected="activeTab === tab.key"
                    v-bind:aria-controls="`tab_pane_${tab.key}`"
                    v-bind:class="{
                        'card-title-tab--active': activeTab === tab.key,
                        'card-title-tab--disabled': tab.disabled,
                    }">
                    <component v-once v-bind:is="tab.icon" class="card-title-tab__icon"/>
                    {{tab.text}}
                    <span v-if="tab?.counter" class="card-title-tab__counter">
                        {{tab.counter}}
                    </span>
                </span>
            </nav>
            <div class="tab-buttons">
                <slot name="customCalendar"></slot>
                <slot name="customFilter"></slot>
            </div>
        </div>

        <div class="tab-pane" role="tabpanel"
            v-for="tab in tabs"
            v-bind:key="`ui_tab_pane_${tab.key}`"
            v-bind:aria-labelledby="`tab_switcher_${tab.key}`"
            v-bind:id="`tab_pane_${tab.key}`"
            v-show="activeTab === tab.key">
            <keep-alive max="2" exclude="tx-root">
                <component
                    v-if="!tab.disabled && (activeTab === tab.key || tab.content.eager)"
                    v-bind:key="`ui_tab_${tab.content.key}_${tab.key}`"
                    v-bind:is="tab.content.component"
                    v-bind="tab.content.props"
                    v-on="$listeners"/> <!-- passthru all listeners to parent -->
            </keep-alive>
        </div>
    </section>
</template>

<script>
export default {
    props: {
        tabs: {
            type: Array,
            required: true,
            default: () => [],
        },
        changeUrlHash: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            activeTab: undefined,
        };
    },

    watch: {
        $route: {
            immediate: true,
            handler({ hash }) {
                this.activeTab = (hash || this.tabs[0].key).replace('#', '');
            },
        },

        activeTab(tabName) {
            if (this.changeUrlHash) {
                // don't trigger route watchers on hash change:
                window.history.replaceState(null, null, `${this.$route.path}#${tabName}`);
                this.updateFilterShow(tabName);
            }
        },
    },

    mounted() {
        this.updateFilterShow(this.activeTab);
    },

    methods: {
        updateFilterShow(tabName) {
            if (tabName === 'events') {
                this.$store.commit('updateFilterShow', true);
                this.$store.commit('updateDyorLinkShow', false);
                this.$store.commit('updateNftItemCountShow', false);
            } else if (tabName === 'swaps') {
                this.$store.commit('updateFilterShow', false);
                this.$store.commit('updateDyorLinkShow', true);
                this.$store.commit('updateNftItemCountShow', false);
            } else if (tabName === 'items') {
                this.$store.commit('updateFilterShow', false);
                this.$store.commit('updateDyorLinkShow', false);
                this.$store.commit('updateNftItemCountShow', true);
            } else {
                this.$store.commit('updateFilterShow', false);
                this.$store.commit('updateDyorLinkShow', false);
                this.$store.commit('updateNftItemCountShow', false);
            }
        },
    },
};
</script>

<style lang="scss">
.card-tabs {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid var(--card-row-separator);
}
.card-title-tabs {
    display: flex;
    align-items: center;
    overflow-x: auto;
    scrollbar-width: none;
    flex-wrap: nowrap;
    flex-direction: row;
    font-weight: 500;
    text-transform: uppercase;
    border-radius: 12px 12px 0 0;
// <<<<<<< HEAD
//     border-bottom: 1px solid var(--card-row-separator);
// =======
// >>>>>>> master
    background: var(--card-header-background);
    color: var(--card-header-color);
    padding: 0 12px;
    &::-webkit-scrollbar {
        display: none;
    }
}
.card-title-tab {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px 16px;
    cursor: pointer;
    transition: .2s all ease;
    white-space: nowrap;
    color: var(--card-header-tab-color);
    user-select: none;
    &__icon {
        fill: currentColor;
        margin-right: 10px;
        width: 24px;
        height: 24px;
    }
    &__counter {
        font-size: 12px;
        padding: 4px 6px;
        margin: -2px 0 -2px 8px;
        border-radius: 6px;
        background-color: var(--body-light-muted-color);
    }
    &:hover, &:focus, &:focus-visible {
        color: var(--body-text-color);
        box-shadow: 0 -2px 0 0 var(--body-muted-text-color) inset;
        outline: none;
        .card-title-tab__counter {
            background-color: var(--address-tag-gray-background);
        }
    }
    &--active {
        &, &:hover, &:focus {
            color: var(--body-text-color);
            box-shadow: 0 -2px 0 0 var(--blue-bright) inset;
        }
        .card-title-tab__icon {
            fill: var(--blue-bright);
        }
        .card-title-tab__counter {
            background-color: var(--address-tag-gray-background);
        }
    }
    &--disabled {
        pointer-events: none;
        color: var(--body-muted-text-color);
        opacity: .45;
    }
}

@media all and (max-width: 480px) {
    .card-title-tab {
        flex-grow: 1;
        flex-basis: 150px;
        padding: 12px 12px;
        font-size: 13px;
        &__icon {
            display: none;
        }
        &__counter {
            font-size: 11px;
            border-radius: 4px;
            padding: 2px 6px;
        }
    }
}
.tab-buttons {
    display: flex;
    align-items: center;
    position: relative;
    z-index: 1020;
}
.tab-buttons:before {
    content: "";
    height: 100%;
    width: 80px;
    position: absolute;
    left: -75px;
    top: 0;
    background: linear-gradient(to left, var(--card-background), transparent);
    pointer-events: none;
}
</style>
